import { z } from 'zod';
import {
	TalkingHeadAgentInfoState,
	TalkingHeadCommandArgumentName,
	TalkingHeadCommandIdn,
	TalkingHeadTrainingErrorType
} from './types';

enum TalkingHeadAgentInfoAcceptance {
	Ignore = 'ignore',
	ErrorsOnly = 'errors-only',
	All = 'all'
}

const TalkingHeadCommandArgumentsSchema = z
	.array(
		z.object({
			name: z.string(),
			value: z.string()
		})
	)
	.transform((args) =>
		Object.fromEntries(args.map((argument) => [argument.name, argument.value]))
	);

const TERMINAL_TRAINING_COMMAND_IDNS = [
	TalkingHeadCommandIdn.OnboardingFinished,
	TalkingHeadCommandIdn.OnboardingError
] as const;

const TalkingHeadTrainingCommandIdnSchema = z.enum(
	TERMINAL_TRAINING_COMMAND_IDNS
);

type TalkingHeadTrainingCommandIdn = z.infer<
	typeof TalkingHeadTrainingCommandIdnSchema
>;

const TalkingHeadTrainingCommandSchema = z.object({
	integration_idn: z.string().optional(),
	connector_idn: z.string().optional(),
	command_idn: TalkingHeadTrainingCommandIdnSchema,
	act_id: z.string().optional(),
	external_event_id: z.string().min(1).optional(),
	arguments: TalkingHeadCommandArgumentsSchema
});

type TalkingHeadTrainingCommand = z.infer<
	typeof TalkingHeadTrainingCommandSchema
>;

const TalkingHeadCommandIdnEnvelopeSchema = z.object({
	command_idn: z.string()
});

const isTerminalTrainingCommandIdn = (
	commandIdn: string
): commandIdn is TalkingHeadTrainingCommandIdn =>
	(TERMINAL_TRAINING_COMMAND_IDNS as readonly string[]).includes(commandIdn);

type TrainingCommandClassification =
	| { kind: 'foreign' }
	| { kind: 'malformed'; issues: z.ZodIssue[] }
	| { kind: 'terminal'; command: TalkingHeadTrainingCommand };

const classifyTrainingCommand = (
	raw: unknown
): TrainingCommandClassification => {
	const idnResult = TalkingHeadCommandIdnEnvelopeSchema.safeParse(raw);
	if (
		!idnResult.success ||
		!isTerminalTrainingCommandIdn(idnResult.data.command_idn)
	) {
		return { kind: 'foreign' };
	}

	const envelope = TalkingHeadTrainingCommandSchema.safeParse(raw);
	if (!envelope.success) {
		return { kind: 'malformed', issues: envelope.error.issues };
	}

	return { kind: 'terminal', command: envelope.data };
};

const TrainingErrorTypeArgumentSchema = z.string().optional();

const OptionalMessageArgumentSchema = z
	.string()
	.optional()
	.transform((value) => value?.trim() || undefined);

const TalkingHeadAgentInfoArgumentsSchema =
	TalkingHeadCommandArgumentsSchema.pipe(
		z.object({
			[TalkingHeadCommandArgumentName.State]: z.string(),
			[TalkingHeadCommandArgumentName.Error]: OptionalMessageArgumentSchema,
			[TalkingHeadCommandArgumentName.ErrorType]:
				TrainingErrorTypeArgumentSchema
		})
	);

const TalkingHeadAgentInfoCommandSchema = z.object({
	command_idn: z.literal(TalkingHeadCommandIdn.AgentInfo),
	external_event_id: z.string().min(1).optional(),
	arguments: TalkingHeadAgentInfoArgumentsSchema
});

type AgentInfoReport = {
	state: TalkingHeadAgentInfoState;
	externalEventId?: string;
	error?: string;
	errorType?: TalkingHeadTrainingErrorType;
	unknownErrorType?: string;
};

const KnownTrainingErrorTypeSchema = z.nativeEnum(TalkingHeadTrainingErrorType);

const splitErrorType = (raw: string | undefined) => {
	const known = KnownTrainingErrorTypeSchema.safeParse(raw);
	return known.success
		? { errorType: known.data, unknownErrorType: undefined }
		: { errorType: undefined, unknownErrorType: raw };
};

const AgentInfoStateSchema = z.nativeEnum(TalkingHeadAgentInfoState);

const parseAgentInfo = (raw: unknown): AgentInfoReport | undefined => {
	const envelope = TalkingHeadAgentInfoCommandSchema.safeParse(raw);
	if (!envelope.success) {
		return undefined;
	}

	const state = AgentInfoStateSchema.safeParse(envelope.data.arguments.state);
	if (!state.success) {
		return undefined;
	}

	return {
		state: state.data,
		externalEventId: envelope.data.external_event_id,
		error: envelope.data.arguments.error,
		...splitErrorType(envelope.data.arguments.error_type)
	};
};

const TalkingHeadNotifyErrorCommandSchema = z.object({
	command_idn: z.literal(TalkingHeadCommandIdn.NotifyError),
	external_event_id: z.string().min(1).optional(),
	arguments: TalkingHeadCommandArgumentsSchema.pipe(
		z.object({
			[TalkingHeadCommandArgumentName.ErrorType]:
				TrainingErrorTypeArgumentSchema,
			[TalkingHeadCommandArgumentName.ErrorMessage]:
				OptionalMessageArgumentSchema
		})
	)
});

type NotifyErrorReport = {
	externalEventId?: string;
	message?: string;
	errorType?: TalkingHeadTrainingErrorType;
	unknownErrorType?: string;
};

const parseNotifyError = (raw: unknown): NotifyErrorReport | undefined => {
	const envelope = TalkingHeadNotifyErrorCommandSchema.safeParse(raw);
	if (!envelope.success) {
		return undefined;
	}

	return {
		externalEventId: envelope.data.external_event_id,
		message: envelope.data.arguments.error_message,
		...splitErrorType(envelope.data.arguments.error_type)
	};
};

type TrainingTerminalOutcome =
	| { type: 'success' }
	| { type: 'ignore' }
	| {
			type: 'failure';
			message: string;
			errorType?: TalkingHeadTrainingErrorType;
			unknownErrorType?: string;
	  };

type TrainingTerminalOptions = {
	externalEventId?: string;
	agentInfo?: TalkingHeadAgentInfoAcceptance;
};

const belongsToRun = (
	commandExternalEventId: string | undefined,
	externalEventId: string | undefined
): boolean =>
	!externalEventId ||
	!commandExternalEventId ||
	commandExternalEventId === externalEventId;

const classifyAgentInfoReport = (
	report: AgentInfoReport,
	agentInfo: TalkingHeadAgentInfoAcceptance,
	externalEventId?: string
): TrainingTerminalOutcome => {
	if (agentInfo === TalkingHeadAgentInfoAcceptance.Ignore) {
		return { type: 'ignore' };
	}

	if (!belongsToRun(report.externalEventId, externalEventId)) {
		return { type: 'ignore' };
	}

	if (report.state === TalkingHeadAgentInfoState.Error) {
		return {
			type: 'failure',
			message:
				report.error ?? 'Training failed: agent_info reported an error state',
			errorType: report.errorType,
			unknownErrorType: report.unknownErrorType
		};
	}

	if (
		agentInfo === TalkingHeadAgentInfoAcceptance.All &&
		report.state === TalkingHeadAgentInfoState.Created
	) {
		return { type: 'success' };
	}

	return { type: 'ignore' };
};

const OnboardingErrorArgumentsSchema = z.object({
	[TalkingHeadCommandArgumentName.Description]: z.string().optional(),
	[TalkingHeadCommandArgumentName.Reason]: z.string().optional()
});

const TERMINAL_CHANNEL_IDNS = [
	TalkingHeadCommandIdn.OnboardingFinished,
	TalkingHeadCommandIdn.OnboardingError,
	TalkingHeadCommandIdn.AgentInfo,
	TalkingHeadCommandIdn.NotifyError
] as const;

const isTerminalChannelIdn = (commandIdn: string): boolean =>
	(TERMINAL_CHANNEL_IDNS as readonly string[]).includes(commandIdn);

const malformedFailure = (detail: unknown): TrainingTerminalOutcome => ({
	type: 'failure',
	message: `Malformed training command: ${JSON.stringify(detail)}`
});

const classifyNotifyError = (
	raw: unknown,
	externalEventId?: string
): TrainingTerminalOutcome => {
	const notifyError = parseNotifyError(raw);
	if (!notifyError) {
		return malformedFailure(raw);
	}
	if (!belongsToRun(notifyError.externalEventId, externalEventId)) {
		return { type: 'ignore' };
	}

	return {
		type: 'failure',
		message: notifyError.message ?? 'Training failed: notify_error received',
		errorType: notifyError.errorType,
		unknownErrorType: notifyError.unknownErrorType
	};
};

const classifyAgentInfo = (
	raw: unknown,
	agentInfo: TalkingHeadAgentInfoAcceptance,
	externalEventId?: string
): TrainingTerminalOutcome => {
	if (agentInfo === TalkingHeadAgentInfoAcceptance.Ignore) {
		return { type: 'ignore' };
	}
	if (!TalkingHeadAgentInfoCommandSchema.safeParse(raw).success) {
		return malformedFailure(raw);
	}

	const report = parseAgentInfo(raw);
	return report
		? classifyAgentInfoReport(report, agentInfo, externalEventId)
		: { type: 'ignore' };
};

const classifyTrainingTerminal = (
	raw: unknown,
	{
		externalEventId,
		agentInfo = TalkingHeadAgentInfoAcceptance.Ignore
	}: TrainingTerminalOptions = {}
): TrainingTerminalOutcome => {
	const envelope = TalkingHeadCommandIdnEnvelopeSchema.safeParse(raw);
	if (!envelope.success || !isTerminalChannelIdn(envelope.data.command_idn)) {
		return { type: 'ignore' };
	}

	if (envelope.data.command_idn === TalkingHeadCommandIdn.NotifyError) {
		return classifyNotifyError(raw, externalEventId);
	}

	if (envelope.data.command_idn === TalkingHeadCommandIdn.AgentInfo) {
		return classifyAgentInfo(raw, agentInfo, externalEventId);
	}

	const classification = classifyTrainingCommand(raw);
	if (classification.kind === 'foreign') {
		return { type: 'ignore' };
	}
	if (classification.kind === 'malformed') {
		return malformedFailure(classification.issues);
	}
	if (
		!belongsToRun(classification.command.external_event_id, externalEventId)
	) {
		return { type: 'ignore' };
	}
	if (
		classification.command.command_idn === TalkingHeadCommandIdn.OnboardingError
	) {
		const args = OnboardingErrorArgumentsSchema.parse(
			classification.command.arguments
		);
		const reason =
			args[TalkingHeadCommandArgumentName.Description] ||
			args[TalkingHeadCommandArgumentName.Reason] ||
			undefined;
		return {
			type: 'failure',
			message: reason ?? 'Training failed: onboarding_error received'
		};
	}

	return { type: 'success' };
};

export {
	classifyTrainingCommand,
	classifyTrainingTerminal,
	isTerminalTrainingCommandIdn,
	parseAgentInfo,
	TalkingHeadAgentInfoAcceptance,
	TalkingHeadTrainingCommandSchema
};
export type { TrainingTerminalOptions, TrainingTerminalOutcome };
